// REVIEW: 未レビュー

import { remarksSchema } from './common';
import {
  buildingNameSchema,
  municipalitiesSchema,
  phoneSchema,
  postalCodeSchema,
  prefectureSchema,
  regexSchema,
  lastUpdatedAtSchema,
} from './company';
import {
  fullWidthCharacter,
  halfWidthAlphanumeric,
  nameRomajiRegex,
  nameKanaRegex,
  halfWidthNumber,
} from './regex';

import { isFromBeforeTo } from '@/utils';
import {
  InferType,
  array,
  boolean,
  number,
  object,
  string,
  date,
} from '@/validations/custom-yup';

/** 企業ID */
export const companyIdSchema = string()
  .label('企業ID');

/** 外部店舗ID */
export const externalShopIdSchema = string()
  .label('外部店舗ID')
  .notRequired()
  .max(100)
  .when(
    'regex',
    ([regex], schema) => (regex ? schema.matches(regex, '外部店舗IDのフォーマットを確認してください。') : schema),
  );

/** 店舗名 */
export const shopNameSchema = string()
  .label('店舗名')
  .required()
  .max(100)
  .matches(fullWidthCharacter, '店舗名に使用できない文字が含まれています。');

/** 店舗サイト URL */
export const homepageUrlSchema = string()
  .label('店舗サイト URL')
  .notRequired()
  .url('店舗サイト URLのフォーマットを確認してください。')
  .max(255);

/** 利用開始日 */
export const shopStartDateSchema = date()
  .label('利用開始日')
  .required()
  .transform((value, originalValue) => (originalValue instanceof Date ? value : undefined));

/** 利用終了日 */
export const shopEndDateSchema = date()
  .label('利用終了日')
  .notRequired()
  .transform((value, originalValue) => (originalValue instanceof Date ? value : undefined))
  .when(
    'shopStartDate',
    ([shopStartDate], schema) => (shopStartDate instanceof Date
      ? schema
        .test(
          'isMoreThanStartsAt',
          '利用終了日は利用開始日以降の日付を入力してください。',
          (value) => (value ? value.getTime() >= shopStartDate.getTime() : true),
        )
      : schema),
  );

/** デフォルト店舗 */
export const defaultShopSchema = boolean()
  .label('デフォルト店舗')
  .required();

/** 店舗位置情報/緯度 */
export const latitudeSchema = number()
  .label('緯度')
  .min(-85, '緯度は±85以内で入力してください。')
  .max(85, '緯度は±85以内で入力してください。');

/** 店舗位置情報/経度 */
export const longitudeSchema = number()
  .label('経度')
  .min(-180, '経度は±180以内で入力してください。')
  .max(180, '経度は±180以内で入力してください。');

const latlngSchema = {
  latitude: latitudeSchema.when('longitude', ([longitude], schema) => (longitude != null ? schema.required() : schema.notRequired())),
  longitude: longitudeSchema.when('latitude', ([latitude], schema) => (latitude != null ? schema.required() : schema.notRequired())),
};

// 循環参照回避のため [['latitude', 'longitude']] を追加
export const latlngTestSchema = object().shape(latlngSchema, [['latitude', 'longitude']]);

/** 契約店ID */
export const contractStoreIdSchema = string()
  .label('契約店ID')
  .notRequired()
  .test(
    'isLength6',
    '契約店IDは6文字で入力してください。',
    (value) => (value ? value.length === 6 : true),
  );

/** 決済金額上限額/回 */
export const maxSettlementAmountSchema = number()
  .label('決済金額上限額/回')
  .notRequired()
  .min(0, '1回あたりの決済上限額は0~99999999以内で入力してください。')
  .max(99999999, '1回あたりの決済上限額は0~99999999以内で入力してください。');

/** ウォレットサービス決済設定ID */
export const walletServiceSettlementSettingIdSchema = number()
  .label('ウォレットサービス決済設定ID')
  .required();

/** 取扱開始日 */
export const settlementServiceStartsAtSchema = date()
  .label('取扱開始日')
  .transform((value, originalValue) => (originalValue instanceof Date ? value : undefined))
  .required();

/** 取扱終了日 */
export const settlementServiceEndsAtSchema = date()
  .label('取扱終了日')
  .transform((value, originalValue) => (originalValue instanceof Date ? value : undefined))
  .notRequired()
  .when(
    'settlementServiceStartsAt',
    ([settlementServiceStartsAt], schema) => schema
      .test(
        '終了日が開始日以降か',
        '終了日は開始日より後の日付を入力してください。',
        (settlementServiceEndsAt) => settlementServiceEndsAt == null || isFromBeforeTo(settlementServiceStartsAt, settlementServiceEndsAt),
      ),
  );

/** 決済サービス */
export const settlementServiceSchema = string()
  .label('決済サービス');

/** 口振口座決済手数料率 */
export const directBankFeeRatioSchema = number()
  .label('口振口座決済手数料率')
  .when(['settlementService'], ([settlementService], schema) => (settlementService === 'BANK_PAY' ? schema.required() : schema.notRequired()))
  .min(0, '手数料率は0以上100.000以下の半角数字を入力してください。')
  .max(100, '手数料率は0以上100.000以下の半角数字を入力してください。')
  .test((value) => !(`${value}`)
    .match(/\.[0-9][0-9][0-9][0-9]/));

/** クレジットカード決済手数料率 */
export const creditFeeRatioSchema = number()
  .label('手数料率')
  .when(['settlementService'], ([settlementService], schema) => (settlementService === 'PSP_GMO' || settlementService === 'PSP_DGFT' ? schema.required() : schema.notRequired()))
  .notRequired()
  .min(0, '手数料率は0以上100.000以下の半角数字を入力してください。')
  .max(100, '手数料率は0以上100.000以下の半角数字を入力してください。')
  .test((value) => !(`${value}`)
    .match(/\.[0-9][0-9][0-9][0-9]/));

/** プリペイド入金手数料率 */
export const prepaidChargeFeeRatioSchema = number()
  .label('プリペイド入金手数料率')
  .when(['settlementService'], ([settlementService], schema) => (settlementService === 'BALANCE_MANAGER' ? schema.required() : schema.notRequired()))
  .min(0, '手数料率は0以上100.000以下の半角数字を入力してください。')
  .max(100, '手数料率は0以上100.000以下の半角数字を入力してください。')
  .test((value) => !(`${value}`)
    .match(/\.[0-9][0-9][0-9][0-9]/));

/** プリペイド利用手数料率 */
export const prepaidSettlementFeeRatioSchema = number()
  .label('プリペイド利用手数料率')
  .when(['settlementService'], ([settlementService], schema) => (settlementService === 'BALANCE_MANAGER' ? schema.required() : schema.notRequired()))
  .notRequired()
  .min(0, '手数料率は0以上100.000以下の半角数字を入力してください。')
  .max(100, '手数料率は0以上100.000以下の半角数字を入力してください。')
  .test((value) => !(`${value}`)
    .match(/\.[0-9][0-9][0-9][0-9]/));

/** GMO-PGショップID */
export const pspGmoShopIdSchema = string()
  .label('GMO-PGショップID')
  .when(
    ['settlementService', 'pspGmoCompUsageFlg'],
    (
      [
        settlementService,
        pspGmoCompUsageFlg,
      ],
      schema,
    ) => (
      settlementService === 'PSP_GMO' && pspGmoCompUsageFlg === false ? schema.required() : schema.notRequired()
    ),
  )
  .matches(halfWidthAlphanumeric, 'GMO-PGショップIDは半角英数字（13文字以内）で入力してください。')
  .max(13, 'GMO-PGショップIDは半角英数字（13文字以内）で入力してください。');

/** GMO-PGショップパスワード */
export const pspGmoShopPasswordSchema = string()
  .label('GMO-PGショップパスワード')
  .when(
    ['settlementService', 'pspGmoCompUsageFlg'],
    (
      [
        settlementService,
        pspGmoCompUsageFlg,
      ],
      schema,
    ) => (
      settlementService === 'PSP_GMO' && pspGmoCompUsageFlg === false ? schema.required() : schema.notRequired()
    ),
  )
  .matches(halfWidthAlphanumeric, 'GMO-PGショップパスワードは半角英数字（10文字以内）で入力してください。')
  .max(10, 'GMO-PGショップパスワードは半角英数字（10文字以内）で入力してください。');

/**
 * GMO-PG 包括利用フラグ
 * フラグがnullの場合は利用すると解釈する仕様になっているため、デフォルト値をtrueに設定する
 */
export const pspGmoCompUsageFlgSchema = boolean()
  .label('GMO-PG 包括利用フラグ')
  .notRequired()
  .default(true);

export const shopSettlementSettingArrayDatumSchema = object({
  walletServiceSettlementSettingId: walletServiceSettlementSettingIdSchema,
  settlementService: settlementServiceSchema,
  shopSettlementServiceStartDate: settlementServiceStartsAtSchema,
  shopSettlementServiceEndDate: settlementServiceEndsAtSchema,
  directBankFeeRatio: directBankFeeRatioSchema,
  creditFeeRatio: creditFeeRatioSchema,
  prepaidChargeFeeRatio: prepaidChargeFeeRatioSchema,
  prepaidSettlementFeeRatio: prepaidSettlementFeeRatioSchema,
  pspGmoShopId: pspGmoShopIdSchema,
  pspGmoShopPassword: pspGmoShopPasswordSchema,
  pspGmoCompUsageFlg: pspGmoCompUsageFlgSchema,
});

export type ShopSettlementSettingArrayDatum = InferType<typeof shopSettlementSettingArrayDatumSchema>;

/** 店舗決済設定 */
export const shopSettlementSettingSchema = array(shopSettlementSettingArrayDatumSchema)
  .label('店舗決済設定')
  .required()
  .default([]);

export const shopNameKanaSchema = string()
  .label('店舗名（カナ）')
  .required()
  .max(64)
  .matches(nameKanaRegex, ({ label }) => `${label}は全角カナ・英数字・一部記号で入力してください。`);

export const shopNameRomajiSchema = string()
  .label('店舗名（ローマ字）')
  .required()
  .max(32)
  .matches(nameRomajiRegex, ({ label }) => `${label}は半角英数字・記号（「"」「,」を除く）で入力してください。`);

export const shopMerchantCategoryCodeSchema = string()
  .label('業種コード')
  .required()
  .length(5)
  .matches(halfWidthNumber, ({ label }) => `${label}は半角数字で入力してください。`);

export const shopUpdateSmartCodeInfoSchema = object({
  nameKana: shopNameKanaSchema,
  nameRomaji: shopNameRomajiSchema,
  merchantCategoryCode: shopMerchantCategoryCodeSchema,
});

export type ShopUpdateSmartCodeInfoSchema = InferType<typeof shopUpdateSmartCodeInfoSchema>;

export const shopEditFormSchema = object()
  .shape({
    companyId: companyIdSchema,
    externalId: externalShopIdSchema,
    name: shopNameSchema,
    phone: phoneSchema,
    postalCode: postalCodeSchema,
    prefecture: prefectureSchema,
    municipalities: municipalitiesSchema,
    buildingName: buildingNameSchema,
    shopStartDate: shopStartDateSchema,
    shopEndDate: shopEndDateSchema,
    contractStoreId: contractStoreIdSchema,
    homepageUrl: homepageUrlSchema,
    defaultShop: defaultShopSchema,
    maxSettlementAmount: maxSettlementAmountSchema,
    shopSettlementSetting: shopSettlementSettingSchema,
    remarks: remarksSchema(),
    regex: regexSchema,
    lastUpdatedAt: lastUpdatedAtSchema,
    shopUpdateSmartCodeInfo: shopUpdateSmartCodeInfoSchema.notRequired(),
  })
  .shape(
    latlngSchema,
    [['latitude', 'longitude']],
  );

export type ShopEditForm = InferType<typeof shopEditFormSchema>;
